import { Command, Settings } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { Link, useParams } from 'react-router';

import { easeOutSmooth, Tooltip } from '@components/primitives';

import { useCommandMenu } from '@components/features/CommandMenu';
import { AppDropdown } from '@components/features/Layout/Navigation/AppDropdown';
import { PciMode } from '@components/features/Layout/Navigation/PciMode';
import { Separator } from '@components/features/Layout/Navigation/Separator';
import css from '@components/features/Layout/Navigation/styles.module.css';
import { TeamDropdown } from '@components/features/Layout/Navigation/TeamDropdown';
import { UpgradeProButton } from '@components/features/Layout/Navigation/UpgradeProButton';
import { usePlain } from '@components/features/Plain';
import { docsHref } from '@utils/docs';

export function TeamAppBreadcrumbs() {
  const { teamUuid, appUuid } = useParams();

  return (
    <>
      <AnimatePresence initial={false} mode="popLayout">
        {teamUuid && <Separator key="team-separator" />}
        {teamUuid && (
          <motion.div
            key={teamUuid}
            className={css.dropdown}
            data-no-shrink
            initial={{ opacity: 0, y: -10, x: 4, filter: 'blur(4px)' }}
            animate={{ opacity: 1, y: 0, x: 0, filter: 'blur(0px)' }}
            exit={{ opacity: 0, y: 10, x: -4 }}
            transition={{
              type: 'spring',
              visualDuration: 0.3,
              bounce: 0.2,
              filter: { duration: 0.3, ease: easeOutSmooth },
            }}
          >
            <TeamDropdown teamUuid={teamUuid} appUuid={appUuid} />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode="popLayout">
        {appUuid && teamUuid && <Separator key="app-separator" />}
        {appUuid && teamUuid && (
          <motion.div
            key={appUuid}
            className={css.dropdown}
            initial={{ opacity: 0, y: -10, x: 4, filter: 'blur(4px)' }}
            animate={{ opacity: 1, y: 0, x: 0, filter: 'blur(0px)' }}
            exit={{ opacity: 0, y: 10, x: -4 }}
            transition={{
              type: 'spring',
              visualDuration: 0.3,
              bounce: 0.2,
              filter: { duration: 0.3, ease: easeOutSmooth },
            }}
          >
            <AppDropdown teamUuid={teamUuid} appUuid={appUuid} />
            <PciMode teamUuid={teamUuid} appUuid={appUuid} />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export function TeamAppLinks() {
  const { teamUuid } = useParams();
  const { isReady, show } = usePlain();
  const { open } = useCommandMenu();

  return (
    <>
      {teamUuid && <UpgradeProButton teamUuid={teamUuid} />}

      <button className={css.textLink} disabled={!isReady} onClick={show}>
        Support
      </button>

      <Link className={css.textLink} to={docsHref()} target="_blank">
        Docs
      </Link>

      <Tooltip side="bottom" content="Command Menu">
        <button className={css.iconButton} onClick={open}>
          <Command aria-hidden />
          <span className="sr-only">Command Menu</span>
        </button>
      </Tooltip>

      {teamUuid && (
        <Tooltip side="bottom" content="Settings">
          <Link className={css.iconButton} to={`/${teamUuid}/settings/team`}>
            <Settings aria-hidden />
            <span className="sr-only">Settings</span>
          </Link>
        </Tooltip>
      )}
    </>
  );
}
